import * as Sentry from '@sentry/react';

/**
 * Configuration options for Sentry initialization
 */
export interface SentryConfig {
  dsn: string;
  environment: string;
  tracesSampleRate: number;
}

/**
 * Initialize Sentry error tracking with comprehensive browser integrations
 * Inspired by Sentry's pinoIntegration approach but adapted for browser use
 * @see https://docs.sentry.io/platforms/javascript/guides/connect/configuration/integrations/pino/
 * @param config - Sentry configuration
 */
export function initializeSentry(config: SentryConfig): void {
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,

    // Configure how traces are sampled
    tracesSampleRate: config.tracesSampleRate,

    integrations: [
      // Automatically capture breadcrumbs for better error context
      Sentry.breadcrumbsIntegration({
        console: true, // Capture console.* calls as breadcrumbs
        dom: true, // Capture DOM events
        fetch: true, // Capture fetch calls
        history: true, // Capture navigation
        xhr: true, // Capture XHR calls
      }),

      // Enhanced browser tracing
      Sentry.browserTracingIntegration({
        enableInp: true, // Track Interaction to Next Paint
      }),

      // Capture unhandled promise rejections
      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: true,
      }),

      // Add context about the HTTP request
      Sentry.httpContextIntegration(),

      // Capture console errors automatically (browser equivalent of pinoIntegration)
      Sentry.captureConsoleIntegration({
        levels: ['error', 'assert'], // Only capture console.error and console.assert
      }),
    ],

    // Configure beforeSend to filter/modify events before sending
    beforeSend(event: Sentry.ErrorEvent, _hint: Sentry.EventHint) {
      // Add custom logic here if needed
      return event;
    },
  });
}

/**
 * Get the Sentry instance for direct access if needed
 */
export { Sentry };
